import richTextOptions from 'components/sections/articles/richTextOptions';
import { Image, RichText, SiteLink } from 'components/shared';
import { trackEvents, trackInWindow } from 'hooks/tracking/track';
import { getLinkHref } from 'lib/utils/helpers';
import React from 'react';
import classNames from 'classnames';
import sizesString from 'lib/sizesString';
import FitGuideCustomVideoPlayer from './customVideoPlayer';

const FitDetail = ({ fitType }) => {
  const {
    title,
    subtitle,
    image,
    description,
    tags,
    caption,
    measurementDescription,
    plp,
    shopButtonText,
    availableWashesCollection,
  } = fitType || {};

  const trackFitProductsLink = t =>
    trackInWindow({
      event: trackEvents.fitGuide,
      page_path: window.location.pathname,
      page_title: document.title,
      element_clicked: `View products - ${t}`,
    });

  return (
    <>
      {/* Side to Side Image Text Box ================== */}
      <div className="mb flex flex-col md:mx-auto md:flex-row-reverse xl:max-w-[81%]">
        <div className="mb-2 grid w-full basis-[59.4%] place-items-center border-beige-50 px-4 py-16 md:border lg:mb-0">
          <div className="flex flex-col items-center text-blue lg:max-w-[22.5rem]">
            <p className="text-body-1 mb-3 uppercase">{subtitle}</p>
            <p className="text-heading-3 mb-4 uppercase">{title}</p>
            <div className="mb-7 flex justify-center">
              {tags?.slice(0, 2)?.map(tag => (
                <span
                  key={tag}
                  className="text-body-1 flex items-center capitalize after:mx-[5px] after:inline-block after:h-[1px] after:w-[10px] after:bg-black last:after:hidden md:text-[12px]"
                >
                  {tag}
                </span>
              ))}
            </div>
            <p className="text-body-3 mb-7 text-center font-normal">{description}</p>
            {!!availableWashesCollection?.items?.length && (
              <div className="mb-7 grid w-fit grid-cols-2 grid-rows-2 gap-x-7 gap-y-1">
                {availableWashesCollection?.items?.map(({ title: washTitle, url }) => (
                  <div key={title + url} className="relative flex gap-2">
                    {/* <Image src={{ url: wash.url }} layout="fill" className="tag-mask-image" /> */}
                    <img
                      loading="lazy"
                      className="tag-mask-image h-5 w-5"
                      src={url}
                      alt={washTitle}
                    />
                    <p className="text-body-1">
                      {washTitle &&
                        washTitle
                          .replace('wash-', '')
                          .replace(/-/g, ' ')
                          .replace(/\b\w/g, letter => letter.toUpperCase())}
                    </p>
                  </div>
                ))}
              </div>
            )}
            {plp && (
              <SiteLink
                className="button w-full"
                onClick={() => trackFitProductsLink(title)}
                href={getLinkHref({
                  __typename: 'PageProductsListing',
                  slug: plp.slug,
                })}
              >
                {shopButtonText}
              </SiteLink>
            )}
          </div>
        </div>
        <div className="relative aspect-[0.78] w-full basis-[40.6%] bg-beige-50 md:aspect-[0.8125]">
          {image && (
            <div className="z-10">
              {image?.video || image?.vimeoUrl ? (
                <FitGuideCustomVideoPlayer
                  src={image?.vimeoUrl || image?.video?.url}
                  image={image.image}
                />
              ) : (
                <Image src={image.image} loading="lazy" sizes={sizesString('100vw', '30vw')} />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Caption and Measurement Description ==================== */}
      <div
        className={classNames(
          'flex-col gap-y-7 md:flex-col-reverse md:gap-y-10 ',
          caption || measurementDescription ? 'pt-8 md:pt-20' : 'min-h-[5rem]',
          !fitType?.detailsCollection?.items?.length ? 'mb-12 md:mb-20' : ''
        )}
      >
        {caption && <p className="text-body-2 mb-7 px-4 lg:text-body-1 md:px-0">{caption}</p>}
        <RichText data={measurementDescription} options={richTextOptions(measurementDescription)} />
      </div>

      {/* Image Grid ================= */}
      {!!fitType?.detailsCollection?.items?.length && (
        <div className="relative mt-12 flex w-full flex-row flex-wrap items-center justify-center gap-x-1.5 gap-y-2 antialiased md:mt-20">
          {fitType?.detailsCollection?.items?.map(({ image: detailImage }, index) => {
            return (
              <div
                className={`
          relative ${
            index === 2 ? 'aspect-[0.78] flex-[1_0_100%]' : 'aspect-[0.72] flex-[1_0_45%]'
          }  overflow-hidden
          md:aspect-[0.8125] md:h-auto
           md:w-[24.375rem] md:max-w-[24.375rem] md:flex-1
          `}
              >
                <Image src={detailImage} />
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default FitDetail;
